import { baseQueryWithReauth } from './baseQuery';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

export type SubagenciaInfo = { id: number; nombre: string };
export type RelacionConBuque = 'TITULAR' | 'SUBAGENCIA' | 'ADMINISTRADOR';

export interface BuqueDetalle {
    subagencia?: SubagenciaInfo | null;
    relacionConBuque?: RelacionConBuque;
    [key: string]: any;
}

export interface BuqueListItem {
    relacionConBuque?: RelacionConBuque;
    [key: string]: any;
}

export const shipsApi = createApi({
    reducerPath: 'ships',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Ships', 'ShipById'],
    endpoints: (builder) => ({
        getParametricData: builder.query({
            query: () => ({ url: 'buques/datos-parametricos' }),
            keepUnusedDataFor: 60 * 60 * 12,
            transformResponse: (response: { data: any }) => response?.data
        }),
        getCountries: builder.query({
            query: () => ({ url: 'buques/datos-parametricos/paises' }),
            keepUnusedDataFor: 60 * 60 * 12,
            transformResponse: (response: { data: { paises: any } }) => response?.data?.paises
        }),
        getCitiesByCountryId: builder.query({
            query: (idPais) => ({ url: `buques/datos-parametricos/paises/${idPais}/ciudades` }),
            keepUnusedDataFor: 60 * 60 * 12,
            transformResponse: (response: { data: { ciudades: any } }) => response?.data?.ciudades
        }),
        shipDataValidation: builder.mutation({
            query: (body) => ({
                url: 'buques/validar-datos',
                body: body,
                method: 'POST'
            })
        }),
        getAssemblers: builder.query({
            query: () => ({ url: 'buques/armadores' }),
            transformResponse: (response: { data: any }) => response?.data?.armadores
        }),
        getTenants: builder.query({
            query: () => ({ url: 'buques/locatarios' }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        getOperators: builder.query({
            query: () => ({ url: 'buques/operadores' }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        getOperatorsByName: builder.query({
            query: (name) => ({ url: `buques/operadores?nombre=${name}` })
        }),
        getAssemblersByName: builder.query({
            query: (name) => ({ url: `buques/armadores?nombre=${name}` })
        }),
        getTenantsByName: builder.query({
            query: (name) => ({ url: `buques/locatarios?nombre=${name}` })
        }),
        saveShip: builder.mutation({
            query: (body) => ({ url: 'buques', method: 'POST', body: body })
        }),
        addCertificate: builder.mutation({
            query: ({ bodyFormData, idBuque, idCertificate }: any) => ({
                url: `buques/${idBuque}/certificados/tipos/${idCertificate}`,
                method: 'POST',
                body: bodyFormData
            })
        }),
        getBuques: builder.query({
            query: ({ filters }) => ({
                url: '/buques',
                params: filters
            }),
            providesTags: ['Ships'],
        }),
        getBuquesFromGiros: builder.query({
            queryFn(args, api, extraOptions, baseQuery) {
                const filters = { ...args };
                delete filters?.estado;
                delete filters?.activo;
                const extraFilters = { ...args };
                return baseQuery({
                    url: `buques?activo=${extraFilters.activo}&estado=${extraFilters.estado[0]}&estado=${extraFilters.estado[1]}`,
                    params: filters
                });
            }
        }),
        getShipByName: builder.query({
            queryFn(args, api, extraOptions, baseQuery) {
                const filters = { ...args };
                delete filters?.estado;
                delete filters?.activo;
                const extraFilters = { ...args };
                return baseQuery({
                    url: `buques?activo=${extraFilters.activo}`,
                    params: filters
                });
            }
        }),
        getCertificatesByIdBuque: builder.query({
            query: (idBuque) => ({ url: `buques/${idBuque}/certificados` }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        deleteCertificate: builder.mutation({
            query: ({ idBuque, uuid }: any) => ({
                url: `buques/${idBuque}/certificados/${uuid}`,
                method: 'DELETE'
            })
        }),
        getBuqueById: builder.query({
            query: (idBuque) => ({
                url: `buques/${idBuque}`
            }),
            providesTags: ['ShipById']
        }),
        assignCertificates: builder.mutation({
            query: (idBuque) => ({
                url: `buques/${idBuque}/asignacion-certificados`,
                method: 'POST'
            })
        }),
        editShip: builder.mutation({
            query: ({ data, idBuque }) => ({
                url: `buques/${idBuque}`,
                method: data?.aprobar || data?.nota ? 'POST' : 'PATCH',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        editCertificate: builder.mutation({
            query: ({ idBuque, idCertificate, data }) => ({
                url: `buques/${idBuque}/certificados/${idCertificate}`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ShipById'])
        }),
        downloadCertificate: builder.query({
            query: ({ idBuque, idCertificate }) => ({
                url: `buques/${idBuque}/certificados/${idCertificate}`
            }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        getShipHistory: builder.query({
            query: ({ id, filters }) => ({
                url: `buques/${id}/historico`,
                params: filters
            })
        }),
        getShipCertificatesHistory: builder.query({
            query: ({ shipId, certificateId, filters }: any) => ({
                url: `buques/${shipId}/tipo-certificado/${certificateId}/historico`,
                params: filters
            })
        }),
        getAgencies: builder.query({
            query: (filters) => ({
                url: 'buques/agencias-maritimas',
                params: filters
            }),
            transformResponse: (response: { data: { agenciasMaritimas: any } }) =>
                response?.data?.agenciasMaritimas
        }),
        createAssembler: builder.mutation({
            query: ({ shipId, data }) => ({
                url: `buques/${shipId}/armadores`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        editAssembler: builder.mutation({
            query: ({ shipId, assemblerId, data }) => ({
                url: `buques/${shipId}/armadores/${assemblerId}`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        postTenant: builder.mutation({
            query: ({ shipId, data }) => ({
                url: `buques/${shipId}/locatarios`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        editTenant: builder.mutation({
            query: ({ shipId, tenantId, data }) => ({
                url: `buques/${shipId}/locatarios/${tenantId}`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        createOperator: builder.mutation({
            query: ({ shipId, data }) => ({
                url: `buques/${shipId}/operadores`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        editOperator: builder.mutation({
            query: ({ shipId, operatorId, data }) => ({
                url: `buques/${shipId}/operadores/${operatorId}`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        pauseShip: builder.mutation({
            query: ({ idBuque, data }) => ({
                url: `buques/${idBuque}/pausar`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        resumeShip: builder.mutation({
            query: ({ idBuque }) => ({
                url: `buques/${idBuque}/reactivar`,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        setAccionRuptura: builder.mutation({
            // Respuesta 200 void: no lleva transformResponse (no hay cuerpo OkResponse)
            query: ({ idBuque, activa }) => ({
                url: `buques/${idBuque}/accion-ruptura`,
                method: 'PATCH',
                body: { activa }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        asignarSubagencia: builder.mutation({
            // Respuesta 200 void: no lleva transformResponse
            query: ({ idBuque, idEmpresaSubagencia }: { idBuque: number; idEmpresaSubagencia: number }) => ({
                url: `buques/${idBuque}/subagencia`,
                method: 'PATCH',
                body: { idEmpresaSubagencia }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        }),
        revocarSubagencia: builder.mutation({
            // Respuesta 200 void: no lleva transformResponse
            query: ({ idBuque }: { idBuque: number }) => ({
                url: `buques/${idBuque}/subagencia`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Ships', 'ShipById'])
        })
    })
});

export const {
    useLazyGetShipByNameQuery,
    useEditTenantMutation,
    useCreateOperatorMutation,
    useEditOperatorMutation,
    usePostTenantMutation,
    useLazyGetAgenciesQuery,
    useGetAgenciesQuery,
    useGetShipCertificatesHistoryQuery,
    useGetShipHistoryQuery,
    useGetBuqueByIdQuery,
    useLazyGetBuquesFromGirosQuery,
    useLazyGetBuquesQuery,
    useLazyDownloadCertificateQuery,
    useEditCertificateMutation,
    useGetCountriesQuery,
    useLazyGetCitiesByCountryIdQuery,
    useEditShipMutation,
    useLazyGetBuqueByIdQuery,
    useAssignCertificatesMutation,
    useGetCitiesByCountryIdQuery,
    useLazyGetCertificatesByIdBuqueQuery,
    useAddCertificateMutation,
    useGetAssemblersQuery,
    useGetTenantsQuery,
    useGetAssemblersByNameQuery,
    useGetTenantsByNameQuery,
    useGetParametricDataQuery,
    useShipDataValidationMutation,
    useLazyGetAssemblersByNameQuery,
    useLazyGetTenantsByNameQuery,
    useSaveShipMutation,
    useGetBuquesQuery,
    useDeleteCertificateMutation,
    useGetOperatorsQuery,
    useGetOperatorsByNameQuery,
    usePauseShipMutation,
    useResumeShipMutation,
    useCreateAssemblerMutation,
    useEditAssemblerMutation,
    useSetAccionRupturaMutation,
    useAsignarSubagenciaMutation,
    useRevocarSubagenciaMutation
} = shipsApi;
